import React, { useEffect, useRef } from 'react';

import { Box, Grid } from '@mui/material';

import EditCourseButton from './editCourseButton';
import PurchaseSection from './purchaseSection';
import PurchaseSectionSkeleton from './purchaseSection/purchaseUnavailable/skeleton';

import Container from '@components/store/container';
import CourseAcademicHeaderBannerDetail from '@components/templates/courseDetail/courseAcademicBanner/header/bannerDetail';
import CourseAcademicHeaderBannerBreadcrumb from '@components/templates/courseDetail/courseAcademicBanner/header/breadCrumb';
import CourseAcademicHeaderBannerHighlightCover from '@components/templates/courseDetail/courseAcademicBanner/header/highlightCover';
import { GradientBackground } from '@components/templates/courseDetail/courseAcademicBanner/style';
import useCourseAcademicBanner from '@hooks/course/courseDetail/useCourseAcademicBanner';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { ICategory } from '@interfaces/category';
import { AcademicCourseEnrollmentStatus, CourseEntity } from '@redux/course/type/courseDetailType';
import { AllVersion } from 'src/types/learningPath';

type CourseAcademicHeaderBannerProps = {
  onHeaderSizeChange: (height: number) => void;
  learningPath?: AllVersion;
  learningPathCategory?: ICategory;
  courseCount?: string;
  courseDetailSeo: CourseEntity;
};

const CourseAcademicHeaderBanner = ({
  onHeaderSizeChange,
  learningPath,
  learningPathCategory,
  courseCount,
  courseDetailSeo,
}: CourseAcademicHeaderBannerProps) => {
  const courseAcademicHeaderBannerRef = useRef<HTMLElement>();
  const courseNameAcademicRef = useRef<HTMLElement>(null);

  const { name, idName, university } = courseDetailSeo;

  const {
    enrollmentStatus,
    examExpiredAt,
    price,
    gtmContentId,
    courseDetailData,
    isCourseDetailLoading,
  } = useCourseDetail();
  const { isEnrolled, isUnAvailable, isUnPurchase, bannerPrimaryColor, bannerSecondaryColor } = useCourseAcademicBanner(
    {
      courseDetailData,
      courseName: name,
      enrollmentStatus: enrollmentStatus as AcademicCourseEnrollmentStatus,
    },
  );

  useEffect(() => {
    if (!courseAcademicHeaderBannerRef?.current?.offsetHeight) {
      return;
    }
    onHeaderSizeChange(courseAcademicHeaderBannerRef?.current?.offsetHeight);
  }, [courseAcademicHeaderBannerRef, onHeaderSizeChange]);

  return (
    <GradientBackground
      primaryColor={bannerPrimaryColor}
      secondaryColor={bannerSecondaryColor}
      ref={courseAcademicHeaderBannerRef}
    >
      <Box
        sx={{
          padding: {
            lg: '24px 0px 64px 0px',
            md: '24px 24px 64px 24px',
            xs: '16px 24px 40px 24px',
          },
        }}
      >
        <Container>
          <Grid container spacing={2} sx={{ mb: { lg: '24px', md: '36px', xs: '16px' } }}>
            <Grid item md={9} xs={12}>
              <CourseAcademicHeaderBannerBreadcrumb
                learningPath={learningPath}
                learningPathCategory={learningPathCategory}
                courseName={name}
                courseCount={courseCount}
                university={university}
              />
            </Grid>
            <Grid
              item
              md={3}
              xs={0}
              sx={{ width: '100%', display: { md: 'flex', xs: 'none' }, justifyContent: 'flex-end' }}
            >
              <EditCourseButton courseIdName={idName} />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'grid',
              padding: { lg: 'unset', md: '0px 48px', xs: 'unset' },
              gridTemplateColumns: {
                xs: '100%',
                md: '1fr',
                lg: '47% 1fr',
              },
              gridTemplateAreas: {
                xs: '"image" "detail" "purchase" "purchase"',
                md: '"image" "detail" "purchase"',
                lg: '"detail detail" "image purchase"',
              },
            }}
          >
            <Box
              sx={{
                gridArea: 'image',
                marginTop: {
                  xs: 'unset',
                  md: 'unset',
                  lg: '40px',
                },
              }}
            >
              <CourseAcademicHeaderBannerHighlightCover courseDetailSeo={courseDetailSeo} />
            </Box>
            <Box
              sx={{
                gridArea: 'detail',
                marginTop: { lg: 'unset', md: '24px', xs: '18px' },
                width: '100%',
              }}
            >
              <CourseAcademicHeaderBannerDetail courseDetailSeo={courseDetailSeo} ref={courseNameAcademicRef} />
            </Box>

            <Box
              sx={{
                gridArea: 'purchase',
                width: '100%',
                mt: { md: '40px', xs: '24px' },
              }}
            >
              {price ? (
                <PurchaseSection
                  name={name}
                  idName={idName}
                  enrollmentStatus={enrollmentStatus as AcademicCourseEnrollmentStatus}
                  examExpiredAt={examExpiredAt}
                  gtmContentId={gtmContentId}
                  price={price}
                  isEnrolled={isEnrolled}
                  isUnAvailable={isUnAvailable}
                  isUnPurchase={isUnPurchase}
                  courseDetailData={courseDetailData}
                  isCourseDetailLoading={isCourseDetailLoading}
                />
              ) : (
                <PurchaseSectionSkeleton />
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </GradientBackground>
  );
};

export default CourseAcademicHeaderBanner;
